import { GetTokenApi } from './GetTokenApi';
import type {
    TokenRequestParams,
    ConsumerTokenRequestParams,
    EnterpriseTokenRequestParams,
} from 'owa-tokenprovider-utils';
import { LogStartPerformanceUsage } from 'owa-analytics-start';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import { NUM_OF_RETRIES, RETRY_DELAY } from './const/constants';
import { fetchTokenFromHostAppForEnterpriseWithRetry } from './utils/fetchTokenFromHostAppForEnterprise';
import { fetchTokenFromHostAppForConsumerWithRetry } from './utils/fetchTokenFromHostAppForConsumer';
import { isBootFeatureEnabled } from 'owa-metatags';
import type { ITokenQueue } from './utils/ITokenQueue';
import type { ITokenCache } from './utils/ITokenCache';
import { type UnifiedTokenCache } from 'auth-token-cache';
import type { MailboxInfo } from 'owa-client-types';

export class GetTokenApiUsingHostApp extends GetTokenApi {
    public readonly LoggerPrefix = 'GetTokenApiUsingHostApp';
    private hostApp: string;

    constructor(
        queue: ITokenQueue,
        cache: ITokenCache,
        unifiedCache: UnifiedTokenCache,
        hostApp?: string
    ) {
        super(queue, cache, unifiedCache);
        this.hostApp = hostApp ?? 'UNKNOWN';
    }

    protected override createDatapoint(
        mailboxInfo: MailboxInfo | undefined
    ): LogStartPerformanceUsage {
        let perfLog: LogStartPerformanceUsage;
        if (this.hostApp === 'native') {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp_Native', mailboxInfo);
        } else if (this.hostApp === 'outlookdesktop') {
            perfLog = new LogStartPerformanceUsage(
                'GetTokenApiUsingHostApp_OutlookDesktop',
                mailboxInfo
            );
        } else if (this.hostApp === 'macoutlook') {
            perfLog = new LogStartPerformanceUsage(
                'GetTokenApiUsingHostApp_MacOutlook',
                mailboxInfo
            );
        } else if (this.hostApp === 'teams') {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp_Teams', mailboxInfo);
        } else {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp', mailboxInfo);
        }
        perfLog.addCustomProperty('hostApp', this.hostApp);
        return perfLog;
    }

    public async getTokenForEnterpriseCallback(
        params: TokenRequestParams,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const enterpriseParams = params as EnterpriseTokenRequestParams;

        let cacheKey: string = '';
        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (enterpriseParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(
                    enterpriseParams.mailboxInfo,
                    enterpriseParams.resource,
                    enterpriseParams.scope,
                    enterpriseParams.targetTenantId,
                    undefined /* childAppId */
                );
            }
        } else {
            cacheKey = this.tokenCache.getCacheKey(enterpriseParams);
        }

        perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCallback`, true);
        const tokenResponse = await fetchTokenFromHostAppForEnterpriseWithRetry(
            perfLog,
            NUM_OF_RETRIES,
            RETRY_DELAY,
            enterpriseParams
        );

        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (enterpriseParams.mailboxInfo) {
                this.unifiedCache.putCachedToken(cacheKey, true, tokenResponse);
            }
        } else {
            this.tokenCache.putCachedToken(cacheKey, enterpriseParams, tokenResponse);
        }

        return tokenResponse;
    }

    public async getTokenForConsumerCallback(
        params: TokenRequestParams,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const consumerParams = params as ConsumerTokenRequestParams;

        let cacheKey: string = '';
        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (consumerParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(
                    consumerParams.mailboxInfo,
                    consumerParams.resource,
                    consumerParams.scope,
                    consumerParams.targetTenantId,
                    undefined /* childAppId */
                );
            }
        } else {
            cacheKey = this.tokenCache.getCacheKey(consumerParams);
        }

        perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback`, true);
        const tokenResponse = await fetchTokenFromHostAppForConsumerWithRetry(
            perfLog,
            NUM_OF_RETRIES,
            RETRY_DELAY,
            consumerParams
        );

        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (consumerParams.mailboxInfo) {
                this.unifiedCache.putCachedToken(cacheKey, false, tokenResponse);
            }
        } else {
            this.tokenCache.putCachedToken(cacheKey, consumerParams, tokenResponse);
        }

        return tokenResponse;
    }
}
